import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../../lib/supabase'
import { useFirm } from '../../../hooks/use-firm'
import type { SearchResult } from '../types/search.types'

export function useGlobalSearch(query: string) {
  const { firmId } = useFirm()
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (!query.trim() || !firmId) {
      setResults([])
      return
    }

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      const term = `%${query}%`

      const [clients, engagements, tasks, files] = await Promise.all([
        (supabase as any).from('clients').select('id, name').eq('firm_id', firmId).ilike('name', term).limit(5),
        (supabase as any).from('engagements').select('id, service_line, tax_year, status').eq('firm_id', firmId).or(`service_line.ilike.${term}`).limit(5),
        (supabase as any).from('tasks').select('id, name, status').eq('firm_id', firmId).ilike('name', term).limit(5),
        (supabase as any).from('files').select('id, original_name, entity_type').eq('firm_id', firmId).ilike('original_name', term).limit(5),
      ])

      const mapped: SearchResult[] = [
        ...(clients.data || []).map((c: any) => ({
          id: c.id,
          type: 'client' as const,
          title: c.name,
          subtitle: undefined,
          path: '/clients'
        })),
        ...(engagements.data || []).map((e: any) => ({
          id: e.id,
          type: 'engagement' as const,
          title: `${e.service_line} - ${e.tax_year}`,
          subtitle: e.status,
          path: '/engagements'
        })),
        ...(tasks.data || []).map((t: any) => ({
          id: t.id,
          type: 'task' as const,
          title: t.name,
          subtitle: t.status,
          path: '/tasks'
        })),
        ...(files.data || []).map((f: any) => ({
          id: f.id,
          type: 'document' as const,
          title: f.original_name,
          subtitle: f.entity_type,
          path: '/documents'
        })),
      ]

      setResults(mapped)
      setLoading(false)
    }, 300)

    return () => clearTimeout(debounceRef.current)
  }, [query, firmId])

  return { results, loading }
}
